/**
 * useRoundEndAnimations Composable
 *
 * Handles all round-end animation orchestration including:
 * - Canasta display and counting (clean left, dirty+wild right)
 * - Card pile formation with natural scatter positioning
 * - Voice-synchronized counting animations
 * - Penalty display (red 3s, hand cards)
 *
 * Uses "natural dealing" positioning where cards appear slightly
 * rotated and offset, like human-dealt cards on a table.
 *
 * @example
 * const { generateNaturalPosition, highlightPileSequentially } = useRoundEndAnimations()
 */

import { ref } from 'vue'
import { useAnimations } from './useAnimations'
import { useSounds } from './useSounds'
import { numberToWords, formatPointsForTTS, formatTeamPenaltiesForTTS } from '../utils/numberToWords'

/**
 * Voice Narration Templates
 * Centralized scripts for all round-end voice announcements
 */
export const NARRATION = {
  canastas: {
    /**
     * Build narration for canastas (when team has any)
     */
    withContent: (
      teamName: string,
      natural: number,
      mixed: number,
      wild: number,
      total: number
    ): string => {
      const parts: string[] = []
      if (natural > 0) {
        parts.push(`${natural} clean canasta${natural > 1 ? 's' : ''}`)
      }
      if (mixed > 0) {
        parts.push(`${mixed} dirty canasta${mixed > 1 ? 's' : ''}`)
      }
      if (wild > 0) {
        parts.push(`${wild} wild canasta${wild > 1 ? 's' : ''}`)
      }
      return `${teamName} has ${parts.join(' and ')} for ${total.toLocaleString()} points.`
    },
    /**
     * Narration when team has no canastas
     */
    empty: (teamName: string): string => `${teamName} has no canastas this round.`,
  },

  cardPoints: {
    /**
     * Narration for card points total
     */
    withContent: (_teamName: string, points: number): string =>
      `Card points total: ${points.toLocaleString()}.`,
    /**
     * Narration when team has no card points
     */
    empty: (teamName: string): string => `${teamName} has no card points.`,
  },

  penalties: {
    /**
     * Narration for red 3s penalty
     */
    red3s: (teamName: string, count: number, points: number): string =>
      `${teamName} has ${count} red three${count > 1 ? 's' : ''} for negative ${points.toLocaleString()} points.`,
    /**
     * Narration for hand cards penalty
     */
    handCards: (count: number, points: number): string =>
      `${count} cards remaining in hand for negative ${points.toLocaleString()} points.`,
    /**
     * Narration when team has no penalties
     */
    none: (teamName: string): string => `${teamName} has no penalties.`,
  },

  summary: {
    /**
     * Narration announcing round winner
     */
    winner: (teamName: string, points: number): string =>
      `${teamName} wins the round with ${points.toLocaleString()} points!`,
    /**
     * Narration for tied round (rare)
     */
    tie: (points: number): string => `It's a tie! Both teams scored ${points.toLocaleString()} points!`,
  },
} as const

/**
 * Card positioning for natural "dealt" appearance
 */
interface NaturalPosition {
  x: number
  y: number
  rotation: number
  zIndex: number
}

/**
 * Generate a random value within a range
 */
function randomInRange(min: number, max: number): number {
  return min + Math.random() * (max - min)
}

/**
 * useRoundEndAnimations Composable
 */
export function useRoundEndAnimations() {
  const { reducedMotion, animationSpeed } = useAnimations()
  const { speak, speakCounting } = useSounds()

  // Animation state
  const isAnimating = ref(false)
  const currentPhase = ref<'idle' | 'canastas' | 'piles' | 'penalties' | 'summary'>('idle')

  /**
   * Generate natural "dealt" position for a card within a container area
   * Cards appear slightly rotated and offset, like human-dealt cards
   *
   * @param baseX - Base X position in container
   * @param baseY - Base Y position in container
   * @param index - Card index (for z-index ordering)
   * @param isStack - Whether card is part of a stack (less rotation)
   */
  function generateNaturalPosition(
    baseX: number,
    baseY: number,
    index: number,
    isStack: boolean = false
  ): NaturalPosition {
    // Per-card randomization
    const rotationRange = isStack ? 3 : 6 // Stacked cards rotate less
    const offsetRange = isStack ? 5 : 10

    return {
      x: baseX + randomInRange(-offsetRange, offsetRange),
      y: baseY + randomInRange(-offsetRange * 0.8, offsetRange * 0.8),
      rotation: randomInRange(-rotationRange / 2, rotationRange / 2),
      zIndex: index,
    }
  }

  /**
   * Generate natural scattered positions for 100-point piles
   * Piles follow L→R order but with organic scatter
   *
   * @param pileCount - Number of piles to position
   * @param containerWidth - Width of container area
   * @param containerHeight - Height of container area
   */
  function generatePilePositions(
    pileCount: number,
    containerWidth: number,
    _containerHeight?: number
  ): NaturalPosition[] {
    const positions: NaturalPosition[] = []
    const pileWidth = 70 // Approximate pile width
    const pileHeight = 100 // Approximate pile height
    const padding = 20

    // Calculate grid layout with natural scatter
    const pilesPerRow = Math.max(3, Math.floor((containerWidth - padding * 2) / (pileWidth + 15)))

    for (let i = 0; i < pileCount; i++) {
      const row = Math.floor(i / pilesPerRow)
      const col = i % pilesPerRow

      // Base grid position
      const baseX = padding + col * (pileWidth + 15) + pileWidth / 2
      const baseY = padding + row * (pileHeight + 10) + pileHeight / 2

      // Add natural scatter
      positions.push({
        x: baseX + randomInRange(-10, 10),
        y: baseY + randomInRange(-7.5, 7.5),
        rotation: randomInRange(-5, 5),
        zIndex: i,
      })
    }

    return positions
  }

  /**
   * Calculate voice duration estimate for counting sequences
   * Used to synchronize pile highlighting with voice
   *
   * @param numbers - Array of numbers to count
   * @param suffix - Optional suffix text
   */
  function estimateVoiceDuration(numbers: number[], suffix: string = ''): number {
    const countingText = numbers.join(', ')
    const fullText = suffix ? `${countingText} ${suffix}` : countingText

    // Base time + per-character estimate
    return 1000 + fullText.length * 150
  }

  /**
   * Build canasta counting numbers (cumulative)
   * Clean canastas: 5, 10, 15... (500pt each = /100)
   * Dirty/Wild combined: 3, 6, 9... or 10, 20... mixed
   *
   * @param canastas - Canasta counts by type
   */
  function buildCanastaCountNumbers(canastas: { natural: number; mixed: number; wild: number }): {
    cleanNumbers: number[]
    dirtyWildNumbers: number[]
    cleanTotal: number
    dirtyWildTotal: number
  } {
    const cleanNumbers: number[] = []
    const dirtyWildNumbers: number[] = []

    let cleanRunning = 0
    let dirtyWildRunning = 0

    // Clean canastas: +5 each (500 / 100)
    for (let i = 0; i < canastas.natural; i++) {
      cleanRunning += 5
      cleanNumbers.push(cleanRunning)
    }

    // Mixed canastas: +3 each (300 / 100)
    for (let i = 0; i < canastas.mixed; i++) {
      dirtyWildRunning += 3
      dirtyWildNumbers.push(dirtyWildRunning)
    }

    // Wild canastas: +10 each (1000 / 100)
    for (let i = 0; i < canastas.wild; i++) {
      dirtyWildRunning += 10
      dirtyWildNumbers.push(dirtyWildRunning)
    }

    return {
      cleanNumbers,
      dirtyWildNumbers,
      cleanTotal: cleanRunning * 100, // Convert back to actual points
      dirtyWildTotal: dirtyWildRunning * 100,
    }
  }

  /**
   * Format number as spoken word for totals
   * e.g., 4005 -> "four thousand and five", 3155 -> "three thousand one hundred and fifty-five"
   *
   * Uses the centralized numberToWords utility for consistent pronunciation.
   *
   * @param points - Point value to format
   */
  function formatPointsForSpeech(points: number): string {
    return numberToWords(points)
  }

  /**
   * Highlight piles sequentially in sync with voice timing
   *
   * @param pileElements - Array of pile DOM elements
   * @param estimatedDuration - Estimated voice duration in ms
   * @param onHighlight - Callback when each pile is highlighted
   */
  async function highlightPilesSequentially(
    pileElements: HTMLElement[],
    estimatedDuration: number,
    onHighlight?: (index: number) => void
  ): Promise<void> {
    if (pileElements.length === 0) return

    const interval = estimatedDuration / pileElements.length

    for (let i = 0; i < pileElements.length; i++) {
      const pile = pileElements[i]
      if (!pile) continue

      // Add counting highlight class
      pile.classList.add('counting')
      onHighlight?.(i)

      // Wait for interval
      await delay(interval)

      // Remove counting, add counted
      pile.classList.remove('counting')
      pile.classList.add('counted')
    }
  }

  /**
   * Animate canasta counting for a team
   * Shows canastas visually and speaks counting sequence
   *
   * @param teamName - Team name for speech
   * @param canastas - Canasta counts
   * @param onCountUpdate - Callback for count updates
   */
  async function animateCanastaCount(
    _teamName: string,
    canastas: { natural: number; mixed: number; wild: number },
    onCountUpdate?: (cleanTotal: number, dirtyWildTotal: number) => void
  ): Promise<{ cleanTotal: number; dirtyWildTotal: number }> {
    const { cleanNumbers, dirtyWildNumbers, cleanTotal, dirtyWildTotal } =
      buildCanastaCountNumbers(canastas)

    // Count clean canastas: "5, 10, 15" (just the numbers)
    if (cleanNumbers.length > 0) {
      await speakCounting(cleanNumbers, '')
    }

    // Count dirty+wild canastas: "3, 6, 9" (just the numbers, no transition phrase)
    if (dirtyWildNumbers.length > 0) {
      await speakCounting(dirtyWildNumbers, '')
    }

    // Update totals
    onCountUpdate?.(cleanTotal, dirtyWildTotal)

    // Announce combined total at the end
    const grandTotal = cleanTotal + dirtyWildTotal
    if (grandTotal > 0) {
      await speak(`${formatPointsForSpeech(grandTotal)}.`, 'counter')
    }

    return { cleanTotal, dirtyWildTotal }
  }

  /**
   * Animate pile counting for a team
   * Highlights piles sequentially while speaking
   *
   * @param pileElements - Array of pile DOM elements
   * @param pileCount - Number of piles (for counting)
   * @param canastaBase - Canasta bonus to add to total
   * @param teamName - Team name for speech
   */
  async function animatePileCount(
    pileElements: HTMLElement[],
    pileCount: number,
    canastaBase: number,
    _teamName?: string
  ): Promise<void> {
    if (pileCount === 0) return

    // Build counting numbers: 1, 2, 3, 4...
    const numbers = Array.from({ length: pileCount }, (_, i) => i + 1)
    const cardPoints = pileCount * 100
    const combinedTotal = canastaBase + cardPoints

    // Calculate voice duration for synchronization
    const voiceDuration = estimateVoiceDuration(
      numbers,
      `${formatPointsForSpeech(cardPoints)}, for a total of ${formatPointsForSpeech(combinedTotal)}`
    )

    // Start highlighting and voice in parallel
    const highlightPromise = highlightPilesSequentially(pileElements, voiceDuration * 0.7)
    const voicePromise = speakCounting(
      numbers,
      `${formatPointsForSpeech(cardPoints)}, for a total of ${formatPointsForSpeech(combinedTotal)}`
    )

    await Promise.all([highlightPromise, voicePromise])
  }

  /**
   * Delay helper
   */
  function delay(ms: number): Promise<void> {
    // Apply animation speed multiplier
    const adjustedMs = reducedMotion.value ? 100 : ms / animationSpeed.value
    return new Promise((resolve) => setTimeout(resolve, adjustedMs))
  }

  /**
   * Team pause (brisk 1 second between teams)
   */
  async function teamPause(): Promise<void> {
    await delay(1000)
  }

  /**
   * Orchestrate a phase by running animation and voice in parallel
   * Waits for BOTH to complete before returning
   *
   * @param animationFn - Animation function that returns a Promise
   * @param voiceText - Text to speak during the animation
   * @param pauseAfter - Whether to add a brief pause after (default: true)
   */
  async function orchestratePhase(
    animationFn: () => Promise<void>,
    voiceText: string,
    pauseAfter: boolean = true
  ): Promise<void> {
    // Run animation and voice in parallel
    await Promise.all([animationFn(), speak(voiceText, 'announcer')])

    // Brief pause for pacing (brisk 1 second)
    if (pauseAfter) {
      await delay(1000)
    }
  }

  /**
   * Speak voice-only phase (for empty phases)
   * Skips visual display but announces via voice
   *
   * @param voiceText - Text to speak
   */
  async function voiceOnlyPhase(voiceText: string): Promise<void> {
    await speak(voiceText, 'announcer')
    await delay(500) // Brief pause after voice-only
  }

  return {
    // Natural positioning
    generateNaturalPosition,
    generatePilePositions,

    // Counting helpers
    buildCanastaCountNumbers,
    formatPointsForSpeech,
    estimateVoiceDuration,

    // Animation functions
    animateCanastaCount,
    animatePileCount,
    highlightPilesSequentially,

    // Phase orchestration
    orchestratePhase,
    voiceOnlyPhase,

    // Timing
    delay,
    teamPause,

    // Voice
    speak,

    // State
    isAnimating,
    currentPhase,
    reducedMotion,
  }
}
